import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { websitePaths } from "../../../routes/websitePaths";
import { useFetchPublicProjectImagesQuery } from "../../../store/api/projectImagesApi";
import "../../../components/website/sections/ProjectCategoryPage.css";

const AllProjects: React.FC = () => {
  const { data: residentialImgs = [] } = useFetchPublicProjectImagesQuery("Residential");
  const { data: commercialImgs  = [] } = useFetchPublicProjectImagesQuery("Commercial");
  const { data: industrialImgs  = [] } = useFetchPublicProjectImagesQuery("Industrial");
  const [activePreview, setActivePreview] = useState<{
    title: string;
    image: string;
    location: string;
  } | null>(null);

  const categories = [
    {
      title: "Residential Projects",
      label: "Homes And Villas",
      count: "120+",
      description:
        "Explore homes, villas, duplex builds, and residential upgrades designed for comfort, functionality, and long-term value.",
      link: websitePaths.projects.residential,
      icon: "bi bi-house-door",
    },
    {
      title: "Commercial Projects",
      label: "Business Spaces",
      count: "80+",
      description:
        "Browse office, retail, and hospitality spaces planned around customer flow, brand presentation, and daily operations.",
      link: websitePaths.projects.commercial,
      icon: "bi bi-building",
    },
    {
      title: "Industrial Projects",
      label: "Operational Sites",
      count: "45+",
      description:
        "View warehouse, utility, and industrial work focused on durability, movement efficiency, and dependable site performance.",
      link: websitePaths.projects.industrial,
      icon: "bi bi-gear-wide-connected",
    },
  ];

  const projects = [
    {
      title: "Luxury Villa Construction",
      category: "Residential",
      location: "Gomti Nagar, Lucknow",
      description:
        "A premium villa project delivered with turnkey planning, strong structural execution, and refined interior detailing.",
      image: residentialImgs[0]?.imageUrl ??
        "https://images.unsplash.com/photo-1600585154363-67eb9e2e2099?auto=format&fit=crop&w=1200&q=80",
    },
    {
      title: "Corporate Office Tower",
      category: "Commercial",
      location: "Hazratganj, Lucknow",
      description:
        "A contemporary office environment planned for workflow efficiency, client presentation, and business-ready delivery.",
      image: commercialImgs[0]?.imageUrl ??
        "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=1200&q=80",
    },
    {
      title: "Warehouse Development",
      category: "Industrial",
      location: "Transport Nagar, Lucknow",
      description:
        "A large-format warehouse project executed for smooth logistics movement, operational safety, and long-term durability.",
      image: industrialImgs[0]?.imageUrl ??
        "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?auto=format&fit=crop&w=1200&q=80",
    },
  ];

  useEffect(() => {
    if (!activePreview) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setActivePreview(null);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [activePreview]);

  return (
    <>
      <section className='project-category-page website-page all-projects-page'>
        <div className='project-category-hero website-page-hero text-center'>
          <div className='container'>
            <p className='website-page-kicker mb-1'>Project Portfolio</p>
            <h1 className='project-page-title website-page-title mb-2'>
              Explore projects across residential, commercial, and industrial
              categories.
            </h1>
            <p className='project-page-subtitle website-page-subtitle mx-auto mb-0'>
              This page helps you quickly choose the project category that
              matches your requirement, then review relevant examples with
              better clarity and direction.
            </p>
          </div>
        </div>

        <div className='container website-page-content'>
          <div className='all-projects-intro mb-5'>
            <div className='all-projects-intro-copy'>
              <span className='website-section-kicker'>Project Categories</span>
              <h2 className='website-section-title mb-2'>
                Choose the project type that best fits your property or business
                need.
              </h2>
              <p className='website-section-subtitle mb-0'>
                Whether you are planning a home, a commercial space, or an
                industrial site, each category below takes you to focused
                examples and more relevant project details.
              </p>
            </div>
          </div>

          <div className='row g-4 mb-5'>
            {categories.map((category) => (
              <div className='col-md-6 col-lg-4' key={category.title}>
                <article className='all-projects-category-card h-100'>
                  <div className='all-projects-category-top'>
                    <div className='project-category-link-icon'>
                      <i className={category.icon}></i>
                    </div>
                    <div className='all-projects-category-meta'>
                      <span className='all-projects-category-label'>
                        {category.label}
                      </span>
                      <strong>{category.count}</strong>
                    </div>
                  </div>
                  <h3>{category.title}</h3>
                  <p>{category.description}</p>
                  <ul className='all-projects-category-points'>
                    <li>Relevant examples only</li>
                    <li>Customer-friendly information</li>
                    <li>Direct next step for inquiry</li>
                  </ul>
                  <div className='project-card-footer'>
                    <Link to={category.link} className='project-card-view-more'>
                      View Category <i className='bi bi-arrow-right-short'></i>
                    </Link>
                  </div>
                </article>
              </div>
            ))}
          </div>

          <div className='all-projects-spotlight mb-5'>
            <div className='all-projects-spotlight-head'>
              <span className='website-section-kicker'>Featured Examples</span>
              <h2 className='website-section-title mb-2'>
                A quick look at the kind of work we deliver.
              </h2>
              <p className='website-section-subtitle mb-0'>
                These examples give you a simple overview before you go deeper
                into a specific category page.
              </p>
            </div>

            <div className='row g-4'>
              {projects.map((project) => (
                <div
                  className='col-md-6 col-lg-4'
                  key={`${project.title}-${project.location}`}
                >
                  <article className='card project-show-card h-100 border-0'>
                    <span
                      className='project-show-image-trigger '
                      style={{ cursor: "pointer" }}
                      onClick={() =>
                        setActivePreview({
                          title: project.title,
                          image: project.image,
                          location: project.location,
                        })
                      }
                      aria-label={`Open large image for ${project.title}`}
                    >
                      <img
                        src={project.image}
                        alt={project.title}
                        className='card-img-top project-show-image'
                      />
                    </span>
                    <div className='card-body d-flex flex-column'>
                      <span className='all-projects-project-badge'>
                        {project.category}
                      </span>
                      <h5 className='card-title mb-2'>{project.title}</h5>
                      <p className='project-show-location mb-2'>
                        <i className='bi bi-geo-alt-fill me-2'></i>
                        {project.location}
                      </p>
                      <p className='card-text mb-3'>{project.description}</p>
                      <Link
                        to={websitePaths.contact}
                        className='btn brand-btn-outline mt-auto align-self-start'
                      >
                        Discuss A Similar Project
                      </Link>
                    </div>
                  </article>
                </div>
              ))}
            </div>
          </div>

          <div className='all-projects-help-strip mb-4'>
            <div className='all-projects-help-item'>
              <h4>Not sure which category fits?</h4>
              <p className='mb-0'>
                Start with the project type closest to your requirement. Our
                team can guide you further.
              </p>
            </div>
            <div className='all-projects-help-item'>
              <h4>Need a direct recommendation?</h4>
              <p className='mb-0'>
                Share your site, scope, or budget range and we will suggest the
                right project path.
              </p>
            </div>
          </div>

          <div className='project-category-cta mt-4'>
            <div>
              <span className='website-section-kicker'>
                Need Project Guidance?
              </span>
              <h3 className='mb-2'>
                Tell us what you want to build and we will guide you to the
                right project category.
              </h3>
              <p className='mb-0'>
                Our team can help you move from idea to planning with clearer
                direction, relevant examples, and the next practical steps.
              </p>
            </div>
            <Link to={websitePaths.contact} className='btn brand-btn-primary'>
              Request Consultation
            </Link>
          </div>
        </div>
      </section>

      {activePreview ? (
        <div
          className='project-image-preview-backdrop'
          role='dialog'
          aria-modal='true'
          aria-label={activePreview.title}
          onClick={() => setActivePreview(null)}
        >
          <div
            className='project-image-preview-modal'
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type='button'
              className='project-image-preview-close'
              onClick={() => setActivePreview(null)}
              aria-label='Close image preview'
            >
              <i className='bi bi-x-lg'></i>
            </button>
            <img
              src={activePreview.image}
              alt={activePreview.title}
              className='project-image-preview-large'
            />
            <div className='project-image-preview-caption'>
              <strong>{activePreview.title}</strong>
              <span>{activePreview.location}</span>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default AllProjects;
